/**
 * CircularIcon — icon rendered inside a circular container with configurable
 * background and border colours. Used for status indicators (armed/disarmed).
 */
import { type FC, memo } from "react";
import "../theme";
import { useUnistyles } from "react-native-unistyles";
import { View } from "./View";
import { Icon } from "./Icon";
import type { IconName } from "../icons/icon-names";

export interface CircularIconProps {
  readonly name: IconName;
  readonly backgroundColor: string;
  readonly borderColor: string;
  readonly iconColor: string;
  /** Outer circle size. Defaults to theme.spacing["8xl"] (32). */
  readonly size?: number;
  /** Inner icon size. Defaults to theme.spacing["5xl"] (20). */
  readonly iconSize?: number;
}

export const CircularIcon: FC<CircularIconProps> = memo(function CircularIcon({
  name,
  backgroundColor,
  borderColor,
  iconColor,
  size,
  iconSize,
}) {
  const { theme } = useUnistyles();
  const outerSize = size ?? theme.spacing["8xl"];
  const innerSize = iconSize ?? theme.spacing["5xl"];

  return (
    <View
      width={outerSize}
      height={outerSize}
      borderRadius={outerSize / 2}
      borderWidth={2}
      alignItems="center"
      justifyContent="center"
      style={{ borderColor, backgroundColor }}
    >
      <Icon name={name} size={innerSize} color={iconColor} />
    </View>
  );
});
